import React, { useRef } from "react";
import ChatPage from "./pages/Chat";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import VerifyEmail from "./pages/VerifyEmail";
import VerificationPending from "./pages/VerificationPending";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Admin from "./pages/Admin";
import { getUser, isLoggedIn } from "./utils/session";
import { CSSTransition, SwitchTransition } from "react-transition-group";

function App() {
  const [screen, setScreen] = React.useState("login");
  const [pendingEmail, setPendingEmail] = React.useState("");
  const [verificationDelivery, setVerificationDelivery] = React.useState({ sent: true, notice: "" });
  const nodeRef = useRef(null);

  const navigateAuth = React.useCallback((nextScreen = "login", details = {}) => {
    if (typeof details.email === "string") {
      setPendingEmail(details.email.trim().toLowerCase());
    }
    if (typeof details.verificationEmailSent === "boolean" || typeof details.notice === "string") {
      setVerificationDelivery({
        sent: details.verificationEmailSent !== false,
        notice: details.notice || ""
      });
    }
    if (["login", "signup", "check-email", "forgot"].includes(nextScreen)) {
      window.history.replaceState({}, "", "/");
    }
    setScreen(nextScreen);
  }, []);

  React.useEffect(() => {
    const path = window.location.pathname;

    if (path.includes("verify-email")) {
      setScreen("verify");
    } else if (path.includes("reset-password")) {
      setScreen("reset");
    } else if (path.includes("signup")) {
      setScreen("signup");
    } else if (path.includes("admin")) {
      if (isLoggedIn() && ["admin", "client_admin"].includes(getUser()?.role)) {
        setScreen("admin");
      } else {
        window.history.replaceState({}, "", "/");
        setScreen("chat");
      }
    }
  }, []);

  if (!isLoggedIn()) {
    const renderScreen = () => {
      switch (screen) {
        case "signup":
          return <Signup onAuth={navigateAuth} />;
        case "check-email":
          return (
            <VerificationPending
              email={pendingEmail}
              emailSent={verificationDelivery.sent}
              initialMessage={verificationDelivery.notice}
              onAuth={navigateAuth}
            />
          );
        case "verify":
          return <VerifyEmail onAuth={navigateAuth} />;
        case "forgot":
          return <ForgotPassword onAuth={navigateAuth} initialEmail={pendingEmail} />;
        case "reset":
          return <ResetPassword onAuth={navigateAuth} />;
        default:
          return <Login onAuth={navigateAuth} initialEmail={pendingEmail} />;
      }
    };

    return (
      <SwitchTransition mode="out-in">
        <CSSTransition
          key={screen}
          nodeRef={nodeRef}
          timeout={300}
          classNames="page"
          unmountOnExit
        >
          <div ref={nodeRef} className="page-wrapper">
            {renderScreen()}
          </div>
        </CSSTransition>
      </SwitchTransition>
    );
  }

  if (screen === "admin" && ["admin", "client_admin"].includes(getUser()?.role)) {
    return (
      <div className="page-wrapper">
        <Admin onBack={() => setScreen("chat")} />
      </div>
    );
  }

  return (
    <SwitchTransition mode="out-in">
      <CSSTransition
        key="chat"
        nodeRef={nodeRef}
        timeout={300}
        classNames="page"
        unmountOnExit
      >
        <div ref={nodeRef} className="page-wrapper">
          <ChatPage onAdmin={() => setScreen("admin")} />
        </div>
      </CSSTransition>
    </SwitchTransition>
  );
}

export default App;
